import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { ArrowLeft, History, Receipt } from "lucide-react";
import WeKitLogo from "@/components/WeKitLogo";
import { supabase } from "@/integrations/supabase/client";

interface Redemption {
  id: string;
  user_name: string | null;
  email: string | null;
  scholarship_code: string | null;
  list_price: number | null;
  discount_applied: number | null;
  final_price: number | null;
  payment_status: string;
  created_at: string;
  coupons: { code: string; label: string; partner_org: string | null } | null;
}

interface AuditEntry {
  id: string;
  coupon_code: string;
  event_type: string;
  actor_email: string | null;
  discount_applied: number | null;
  details: any;
  created_at: string;
}

const eventColor: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  create: "default",
  update: "secondary",
  activate: "default",
  deactivate: "destructive",
  expire: "destructive",
  redeem: "outline",
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const summarizeDetails = (entry: AuditEntry) => {
  const d = entry.details;
  if (!d) return "—";
  if (entry.event_type === "redeem") {
    return d.mode === "full_scholarship"
      ? "Full scholarship unlock"
      : `Paid via Razorpay${d.razorpay_payment_id ? ` (${d.razorpay_payment_id})` : ""}`;
  }
  if (entry.event_type === "create") {
    return `${d.label || ""} · ${d.discount_type} ${d.discount_value ?? ""}`;
  }
  if (d.before && d.after) {
    const changes: string[] = [];
    for (const k of Object.keys(d.after)) {
      if (JSON.stringify(d.before[k]) !== JSON.stringify(d.after[k])) {
        changes.push(`${k}: ${d.before[k]} → ${d.after[k]}`);
      }
    }
    return changes.join(", ") || "—";
  }
  return JSON.stringify(d);
};

const AdminCouponHistory = () => {
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [{ data: pays }, { data: logs }] = await Promise.all([
        supabase
          .from("payments")
          .select(
            "id, user_name, email, scholarship_code, list_price, discount_applied, final_price, payment_status, created_at, coupons:coupon_id(code, label, partner_org)",
          )
          .not("coupon_id", "is", null)
          .order("created_at", { ascending: false })
          .limit(500),
        supabase
          .from("coupon_audit_log")
          .select("*")
          .order("created_at", { ascending: false })
          .limit(500),
      ]);
      setRedemptions((pays as unknown as Redemption[]) || []);
      setAudit((logs as AuditEntry[]) || []);
      setLoading(false);
    };
    load();
  }, []);

  const successful = redemptions.filter((r) => r.payment_status === "success");
  const totalSaved = successful.reduce(
    (s, r) => s + (r.discount_applied || 0),
    0,
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur">
        <div className="flex items-center justify-between px-4 py-3 max-w-6xl mx-auto">
          <WeKitLogo size="sm" />
          <Badge variant="outline">Admin</Badge>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin/coupons">
              <ArrowLeft className="h-4 w-4" /> Coupons
            </Link>
          </Button>
          <h1 className="font-display text-2xl font-bold text-foreground">
            Coupon History & Audit Log
          </h1>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-border/50">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Total redemptions</p>
              <p className="text-2xl font-bold text-foreground">
                {successful.length}
              </p>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Discount given</p>
              <p className="text-2xl font-bold text-foreground">
                ₹{totalSaved.toLocaleString("en-IN")}
              </p>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Audit events</p>
              <p className="text-2xl font-bold text-foreground">{audit.length}</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="redemptions">
          <TabsList>
            <TabsTrigger value="redemptions">
              <Receipt className="h-4 w-4" /> Redemptions
            </TabsTrigger>
            <TabsTrigger value="audit">
              <History className="h-4 w-4" /> Audit log
            </TabsTrigger>
          </TabsList>

          <TabsContent value="redemptions">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="text-lg">
                  Who used each NGO code
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-muted-foreground text-sm">Loading…</p>
                ) : redemptions.length === 0 ? (
                  <p className="text-muted-foreground text-sm">
                    No coupon redemptions yet.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>When</TableHead>
                          <TableHead>Code</TableHead>
                          <TableHead>Partner</TableHead>
                          <TableHead>Student</TableHead>
                          <TableHead>Discount</TableHead>
                          <TableHead>Paid</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {redemptions.map((r) => (
                          <TableRow key={r.id}>
                            <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                              {formatDate(r.created_at)}
                            </TableCell>
                            <TableCell className="font-mono font-semibold">
                              {r.coupons?.code || r.scholarship_code || "—"}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {r.coupons?.partner_org || "—"}
                            </TableCell>
                            <TableCell>
                              <div className="font-medium text-foreground">
                                {r.user_name || "—"}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {r.email}
                              </div>
                            </TableCell>
                            <TableCell className="text-foreground">
                              ₹{(r.discount_applied || 0).toLocaleString("en-IN")}
                            </TableCell>
                            <TableCell className="text-foreground">
                              ₹{(r.final_price || 0).toLocaleString("en-IN")}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  r.payment_status === "success"
                                    ? "default"
                                    : r.payment_status === "pending"
                                      ? "secondary"
                                      : "destructive"
                                }
                                className="text-xs"
                              >
                                {r.payment_status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="audit">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="text-lg">All coupon events</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-muted-foreground text-sm">Loading…</p>
                ) : audit.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No events yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>When</TableHead>
                          <TableHead>Event</TableHead>
                          <TableHead>Code</TableHead>
                          <TableHead>Actor</TableHead>
                          <TableHead>Details</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {audit.map((e) => (
                          <TableRow key={e.id}>
                            <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                              {formatDate(e.created_at)}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={eventColor[e.event_type] || "outline"}
                                className="text-xs capitalize"
                              >
                                {e.event_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono font-semibold">
                              {e.coupon_code}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {e.actor_email || "system"}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-xs max-w-md break-words">
                              {summarizeDetails(e)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminCouponHistory;
